// src/pages/ResetPasswordPage.tsx
import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'

export default function ResetPasswordPage() {
  const [error, setError] = useState<string>()
  const [message, setMessage] = useState<string>()
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const email = searchParams.get('email') ?? ''

  // Handle recovery token in URL
  useEffect(() => {
    // This handles the Supabase auth redirect with hash parameters
    const hashParams = new URLSearchParams(location.hash.substring(1))
    const type = hashParams.get('type')
    const accessToken = hashParams.get('access_token')
    
    if (type === 'recovery' && accessToken) {
      console.log('Recovery session detected')
      // The access_token is automatically handled by Supabase
    }
  }, [location])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(undefined)
    setMessage(undefined)
    setLoading(true)
    
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      })
      
      if (updateError) {
        setError(updateError.message)
      } else {
        setMessage('Password updated successfully!')
        // Navigate to login after a brief delay
        setTimeout(() => navigate('/login'), 1500)
      }
    } catch (err) {
      console.error('Password update error:', err)
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/15 blur-[120px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10"></div>
      
      <div className="w-full max-w-md mb-6 relative z-10">
        <Button 
          as={Link} 
          to="/login" 
          variant="ghost" 
          className="flex items-center text-gray-700 hover:text-gray-950 transition-colors"
        >
          <ArrowLeft size={18} className="mr-1" />
          Back to Login
        </Button>
      </div>

      <Card className="w-full max-w-md glass-card shadow-lg z-10">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-display font-bold text-gray-950">
            Reset Your <span className="gradient-text">Password</span>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                disabled
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="mt-1 w-full p-3 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm"
                  required
                />
                <button 
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            {error && <p className="text-red-600 text-sm">{error}</p>}
            {message && <p className="text-green-600 text-sm">{message}</p>}
            
            <Button 
              type="submit"
              disabled={!password || loading} 
              fullWidth
              className="bg-black hover:bg-gray-900 text-white font-medium !rounded-full shadow-md hover:shadow-lg transition-all duration-300"
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}